// =============================================================================
// Analytics wrapper — Google Tag Manager first.
//
// GTM (GTM-MDSCW2JJ) is installed in public/index.html. Every event here is a
// `dataLayer.push({ event, ...params })` so GTM owns the tags (GA4, HubSpot,
// ads, consent). If GTM is blocked or missing, we fall back to loading gtag.js
// for GA4 directly so nothing is lost.
//
// Event schema (custom event names):
//   page_view                  {page_path, page_location, page_title}  — every SPA route change
//   assessment_intro_view      {mode: 'full'|'quick'}
//   assessment_start_clicked   {mode}
//   assessment_context_complete {scale, industry, mode}
//   assessment_pillar_complete {pillar_id, pillar_index, mode}
//   assessment_results_view    {overall_level, bottleneck, mode}
//   assessment_lead_submitted  {overall_level, bottleneck, mode, company_size}
//   assessment_pdf_downloaded  {overall_level, bottleneck}
//   assessment_cta_clicked     {cta}
//   assessment_reset           {}
//   tos_inquiry_submit         {track, stage, need}
//   lead_form_submitted        {intent_category}
// Recommended GTM wiring: see docs/GTM_SETUP.md.
// =============================================================================

const DEFAULT_GA4_ID = 'G-JW5HCREBYQ';
const GA4_ID = process.env.REACT_APP_GA4_ID || DEFAULT_GA4_ID;
export const GTM_ID = process.env.REACT_APP_GTM_ID || 'GTM-MDSCW2JJ';

const isBrowser = typeof window !== 'undefined';
const isPrerender = () => isBrowser && window.__DD_PRERENDER__ === true;

/** True once GTM's container script has executed. */
export function gtmLoaded() {
  return isBrowser && !!(window.google_tag_manager && window.google_tag_manager[GTM_ID]);
}

/** Push to the dataLayer (creates it if the GTM snippet hasn't run yet). */
export function pushDataLayer(payload) {
  if (!isBrowser || isPrerender()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// ── GA4 fallback (only when GTM never loads, e.g. blocked) ─────────────────────────────
let gtagPromise = null;
function loadGtagFallback() {
  if (!isBrowser || !GA4_ID || isPrerender()) return Promise.resolve(false);
  if (gtagPromise) return gtagPromise;
  gtagPromise = new Promise((resolve) => {
    if (window.gtag) { resolve(true); return; }
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    s.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', GA4_ID, { send_page_view: false, anonymize_ip: true });
      resolve(true);
    };
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return gtagPromise;
}

const FALLBACK_GRACE_MS = 4000;
let fallbackDecided = false;
let useFallback = false;
function decideFallback() {
  if (fallbackDecided || !isBrowser) return;
  // give the GTM snippet a few seconds; if it never executes, GA4 direct
  fallbackDecided = 'pending';
  setTimeout(() => {
    fallbackDecided = true;
    useFallback = !gtmLoaded(); // container never executed (blocked / missing) → GA4 direct
  }, FALLBACK_GRACE_MS);
}

/**
 * Track an event. Fire-and-forget; safe to call anywhere.
 * Always pushes to the dataLayer (GTM); additionally sends via gtag when GTM is unavailable.
 */
export function track(name, params = {}) {
  if (!isBrowser || isPrerender()) return;
  pushDataLayer({ event: name, ...params });
  decideFallback();
  if (fallbackDecided === true && useFallback) {
    loadGtagFallback().then((ok) => { if (ok) { try { window.gtag('event', name, params); } catch { /* silent */ } } });
  }
}

/** SPA page view — called by GtmRouteTracker on every route change (incl. first paint). */
export function trackPageView({ path, title }) {
  if (!isBrowser || isPrerender()) return;
  const payload = {
    event: 'page_view',
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
  };
  pushDataLayer(payload);
  decideFallback();
  if (fallbackDecided === true && useFallback) {
    loadGtagFallback().then((ok) => { if (ok) { try { window.gtag('event', 'page_view', payload); } catch { /* silent */ } } });
  }
}

export function trackIntroView(mode) {
  track('assessment_intro_view', { mode });
}

export const GA4_MEASUREMENT_ID = GA4_ID;
